import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  CanActivateFn,
  Router
} from '@angular/router';
import Keycloak, { KeycloakInstance } from 'keycloak-js';
import { createAuthGuard, AuthGuardData } from 'keycloak-angular';
import { getCleanRedirectUri, hasAuthErrorHash, removeAuthErrorHashFromCurrentUrl } from './auth-url.utils';

const LOGIN_REDIRECT_MARKER_KEY = 'ina.auth.loginRedirect';
const LOGIN_REDIRECT_LOOP_WINDOW_MS = 15000;
const MAX_AUTOMATIC_LOGIN_ATTEMPTS = 2;

/**
 * Función que decide si se permite acceso a la ruta.
 * Si no está autenticado, redirige a Keycloak.
 */
const isAccessAllowed = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authData: AuthGuardData
): Promise<boolean | UrlTree> => {
  const { authenticated } = authData;
  const hadAuthErrorHash = hasAuthErrorHash();

  removeAuthErrorHashFromCurrentUrl();

  if (authenticated) {
    clearLoginRedirectMarker();
    return true;
  }

  // No autenticado → mandamos a login
  const keycloak = inject(Keycloak) as KeycloakInstance;
  const router = inject(Router);
  const redirectUri = getCleanRedirectUri(state.url);
  let marker = readLoginRedirectMarker();

  if (hadAuthErrorHash) {
    clearLoginRedirectMarker();
    marker = null;
  }

  if (isLoopingLogin(marker, redirectUri)) {
    clearLoginRedirectMarker();
    return router.createUrlTree(['/session-expired'], { queryParams: { redirect: state.url } });
  }

  rememberLoginRedirect(redirectUri, marker);

  window.location.replace(keycloak.createLoginUrl({ redirectUri }));

  return false;
};

export const canActivateAuth = createAuthGuard<CanActivateFn>(isAccessAllowed);

function isLoopingLogin(marker: LoginRedirectMarker | null, redirectUri: string): boolean {
  return marker?.redirectUri === redirectUri
    && Date.now() - marker.at < LOGIN_REDIRECT_LOOP_WINDOW_MS
    && marker.attempts >= MAX_AUTOMATIC_LOGIN_ATTEMPTS;
}

function rememberLoginRedirect(redirectUri: string, marker: LoginRedirectMarker | null): void {
  try {
    const isSameRecentRedirect = marker?.redirectUri === redirectUri
      && Date.now() - marker.at < LOGIN_REDIRECT_LOOP_WINDOW_MS;
    const attempts = isSameRecentRedirect ? marker.attempts + 1 : 1;
    sessionStorage.setItem(LOGIN_REDIRECT_MARKER_KEY, JSON.stringify({ redirectUri, at: Date.now(), attempts }));
  } catch {
    // Ignore storage failures and let Keycloak handle the redirect.
  }
}

type LoginRedirectMarker = { redirectUri: string; at: number; attempts: number };

function readLoginRedirectMarker(): LoginRedirectMarker | null {
  try {
    const raw = sessionStorage.getItem(LOGIN_REDIRECT_MARKER_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed) {
      return null;
    }

    return {
      redirectUri: parsed.redirectUri,
      at: parsed.at,
      attempts: parsed.attempts ?? 1,
    };
  } catch {
    return null;
  }
}

function clearLoginRedirectMarker(): void {
  try {
    sessionStorage.removeItem(LOGIN_REDIRECT_MARKER_KEY);
  } catch {
    // Ignore storage failures.
  }
}
